import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Avatar,
  Chip,
  IconButton,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  InputAdornment,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useBoard } from '../../hooks/useBoard';
import { useDatabase, askChatGPT } from '../../hooks/useDatabase';
import { BoardMemberModal } from './BoardMemberModal';
import { BoardConversation } from './BoardConversation';
import { createDefaultBoardMembers } from '../../utils/boardAI';

export function Board() {
  const {
    board,
    members,
    loading,
    error,
    addBoardMember,
    updateBoardMember,
    deleteBoardMember,
  } = useBoard();

  const db = useDatabase();

  const [openMemberModal, setOpenMemberModal] = useState(false);
  const [openConversation, setOpenConversation] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [allInitiatives, setAllInitiatives] = useState<any[]>([]);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [allTasks, setAllTasks] = useState<any[]>([]);

  // Load all data for AI search
  useEffect(() => {
    const loadData = async () => {
      try {
        const [contactsResult, initiativesResult, eventsResult, tasksResult] = await Promise.all([
          db.getContacts(),
          db.getInitiatives(),
          db.getEvents(),
          db.getTasks(),
        ]);

        if (contactsResult.data) setAllContacts(contactsResult.data);
        if (initiativesResult.data) setAllInitiatives(initiativesResult.data);
        if (eventsResult.data) setAllEvents(eventsResult.data);
        if (tasksResult.data) setAllTasks(tasksResult.data);
      } catch (err) {
        console.error('Error loading data for AI search:', err);
      }
    };

    loadData();
  }, [db]);

  const handleSimpleSearch = async () => {
    if (!searchQuery.trim()) {
      setAiError('Please enter a question for the board');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiResponse(null);

    try {
      const stockPrompt = `You are the internal Board of Directors, which guides key decisions across both professional and personal life. This Board currently includes:

-Peter Thiel: Contrarian thinking, history-focused, studied tactician
-Gandalf: Ancient wisdom, moral clarity, strategic foresight
-The Architect (from The Matrix): Systems thinking, cold logic, confidence of ability
-Rick Rubin: Creative intuition, artistic vision, cultural relevance
-Leonardo Da Vinci: Renaissance thinking, interdisciplinary genius, curiosity
-Miyamoto Musashi: The legendary samurai and philosopher would bring a disciplined yet creative approach, encouraging mastery, adaptability, and strategic artistry.

Your role is to provide dramatic reports and strategic insights as the board members themselves. When analyzing questions, each board member should speak in their own voice and character:

Peter Thiel: Contrarian thinking, zero-to-one innovation, contrarian wisdom
Gandalf: Ancient wisdom, moral clarity, strategic foresight
The Architect: Systems thinking, deterministic logic, grand design
Rick Rubin: Creative intuition, artistic vision, cultural relevance, kindness
Leonardo Da Vinci: Renaissance thinking, interdisciplinary genius, curiosity
Miyamoto Musashi: Strategic discipline, mastery, adaptability, warrior philosophy

Write in dramatic script/dialog format with each board member speaking directly. Begin each board member's response with their name. Reference any relevant data or documents as if they are being presented to the board. Maintain gravitas and formality appropriate for a high-level advisory board. Add pomp and circumstance when appropriate.

The board should provide collective wisdom, individual perspectives, and dramatic strategic insights. When possible, the board should deliberate (silently) and then share a unifed report. Provde a summary of the board's thoughts and recommendations, including the names of the board members who contributed to the report and how they voted. A desenting opinion should be included if there is one, and it should be voted upon (silently) who writes the dissenting opinion.`;

      const enhancedQuery = `${stockPrompt}\n\nUser Question: ${searchQuery}`;

      const response = await askChatGPT({
        userQuery: enhancedQuery,
        contacts: allContacts,
        initiatives: allInitiatives,
        events: allEvents,
        tasks: allTasks,
        instructions: 'You are a strategic board advisor. Provide comprehensive analysis and actionable recommendations.',
      });

      setAiResponse(response);
    } catch (err: any) {
      console.error('AI Search Error:', err);
      if (err.message.includes('quota exceeded') || err.message.includes('429')) {
        setAiError('OpenAI quota exceeded. Please check your billing and try again later.');
      } else if (err.message.includes('401') || err.message.includes('API key')) {
        setAiError('OpenAI API key is invalid or missing. Please check your configuration.');
      } else {
        setAiError(err?.message || 'Failed to get response from AI');
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddMember = async (memberData: any) => {
    const result = await addBoardMember(memberData);
    if (result.error) {
      setMemberError(result.error);
      return result;
    }
    setOpenMemberModal(false);
    setMemberError(null);
    return result;
  };

  const handleEditMember = async (memberData: any) => {
    if (!editingMember) return { data: null, error: 'No member selected' };
    
    const result = await updateBoardMember(editingMember.id, memberData);
    if (result.error) {
      setMemberError(result.error);
      return result;
    }
    setOpenMemberModal(false);
    setEditingMember(null);
    setMemberError(null);
    return result;
  };

  const handleDeleteMember = async (memberId: string) => {
    const result = await deleteBoardMember(memberId);
    if (result.error) {
      setMemberError(result.error);
    }
    return result;
  };

  const handleAddDefaultMembers = async () => {
    const defaultMembers = createDefaultBoardMembers();
    for (const member of defaultMembers) {
      await addBoardMember(member);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Board">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Loading your board...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Board of Directors">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {memberError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {memberError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {board?.name || 'Board of Directors'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Ask the Catalyze Board..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ChatIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={handleSimpleSearch}
              disabled={aiLoading}
              startIcon={aiLoading ? <CircularProgress size={20} /> : <ChatIcon />}
            >
              {aiLoading ? 'Asking...' : 'Ask Catalyze Board'}
            </Button>
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={() => setOpenConversation(true)}
              disabled={members.length === 0}
            >
              Start Consultation
            </Button>
          </Box>
        </Box>

        {aiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {aiError}
          </Alert>
        )}

        {aiResponse && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>AI Response:</Typography>
            <Typography>{aiResponse}</Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Board Members Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader
                title="Board Members"
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {members.length === 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddDefaultMembers}
                      >
                        Add Sample Members
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenMemberModal(true)}
                    >
                      Add Member
                    </Button>
                  </Box>
                }
              />
              <CardContent>
                {members.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No board members yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add board members to start getting strategic advice
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleAddDefaultMembers}
                    >
                      Add Sample Members
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {members.map((member, index) => (
                      <React.Fragment key={member.id}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {member.name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {member.name}
                                </Typography>
                                {member.role && (
                                  <Chip
                                    label={member.role}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {member.personality.traits.join(', ')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {member.expertise.slice(0, 3).map((expertise) => (
                                    <Chip
                                      key={expertise}
                                      label={expertise}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  ))}
                                  {member.expertise.length > 3 && (
                                    <Chip
                                      label={`+${member.expertise.length - 3} more`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingMember(member);
                                  setOpenMemberModal(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteMember(member.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < members.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Quick Actions" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenMemberModal(true)}
                    fullWidth
                  >
                    Add Board Member
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ChatIcon />}
                    onClick={() => setOpenConversation(true)}
                    disabled={members.length === 0}
                    fullWidth
                  >
                    Start Consultation
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardHeader title="Board Stats" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Members</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {members.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Active Members</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {members.filter(m => m.is_active).length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Board Member Modal */}
        <BoardMemberModal
          open={openMemberModal}
          onClose={() => {
            setOpenMemberModal(false);
            setEditingMember(null);
            setMemberError(null);
          }}
          onSave={editingMember ? handleEditMember : handleAddMember}
          member={editingMember}
          isEditing={!!editingMember}
        />

        {/* Board Conversation */}
        {board && (
          <BoardConversation
            boardId={board.id}
            open={openConversation}
            onClose={() => setOpenConversation(false)}
          />
        )}
      </Box>
    </DashboardLayout>
  );
} 